import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { useConfigService, ExperienceContext } from '@thd-nucleus/experience-context';
import {
  Button,
  LoadingButton,
  Typography
} from '@one-thd/sui-atomic-components';
import { TRACK, SCREENS, FORK_IN_THE_ROADS_FS_KEY } from '../../AddToConstants';
import { AddToContext } from '../../AddToContext';
import { triggerAnalytics } from '../../AddToHelpers';

export const IntentsPreviewFooter = ({
  handleAccept,
  loading,
  setIsAutoResolving,
  setSelectionStrategy,
  isProjectEmpty = true
}) => {
  const fsForkInTheRoadsEnabled = useConfigService(FORK_IN_THE_ROADS_FS_KEY);
  const {
    setScreen,
    previousScreen
  } = useContext(AddToContext);
  const { channel } = useContext(ExperienceContext);
  const isMobile = channel === 'mobile';

  const handleAutoResolve = (selectionStrategy) => {
    setIsAutoResolving(true);
    setSelectionStrategy(selectionStrategy);
  };

  // Show fallback UI if the feature switch Fork in the Roads is disabled,
  // or if the project already has materials (Choose for Me only applies to empty projects).
  if (!fsForkInTheRoadsEnabled || !isProjectEmpty) {
    return (
      <div className="sui-flex sui-flex-col sui-justify-between sui-gap-2">
        <LoadingButton
          fullWidth
          variant="primary"
          value="Step 3: Save and Review Matches"
          loading={loading}
          onClick={handleAccept}
        >Step 3: Save and Review Matches
        </LoadingButton>
        <Button
          variant="secondary"
          fullWidth
          onClick={() => {
            // 10.4.1 Customize Your List Overlay Exit/Cancel - track when going back via Back button
            triggerAnalytics(TRACK.LIST_BUILDER_CUSTOMIZE_LIST_OVERLAY, {
              screen: SCREENS.GENERATED_LIST_INTENTS_PREVIEW
            });

            // Navigate back to the previous screen, skipping the loading animation
            setScreen(previousScreen === SCREENS.AI_LOADING_ANIMATION ? SCREENS.DEFAULT : (previousScreen || SCREENS.DEFAULT));
          }}
        >Back
        </Button>
      </div>
    );
  }

  return (
    <div className="sui-flex sui-flex-col sui-justify-between sui-gap-2">
      <Typography variant={isMobile ? 'body-sm' : 'h5'} weight="bold">
        How would you like to make product selections?
      </Typography>
      <div
        className="sui-flex sui-gap-4 sui-flex-row sui-items-center sui-h-full sui-rounded-md"
      >
        <div className="sui-w-full sui-flex sui-flex-col sui-gap-1 sui-items-center">
          <LoadingButton
            fullWidth
            variant="primary"
            value="Select"
            loading={loading}
            onClick={() => {
              triggerAnalytics(TRACK.LIST_BUILDER_PREVIEW_LIST_FORK_SELECTION, {
                selection: 'ill-choose'
              });
              handleAutoResolve('MANUAL_SELECTION');
            }}
          >
            I&apos;ll choose
          </LoadingButton>
          <Typography align="center" variant="body-xs">
            Review & pick each match
          </Typography>
        </div>
        <div className="sui-w-full sui-flex sui-flex-col sui-gap-1 sui-items-center">
          <Button
            fullWidth
            variant="primary"
            value="Select"
            onClick={() => {
              triggerAnalytics(TRACK.LIST_BUILDER_PREVIEW_LIST_FORK_SELECTION, {
                selection: 'choose-for-me'
              });
              handleAutoResolve('AUTO_SELECTION');
            }}
          >
            Choose for me
          </Button>
          <Typography align="center" variant="body-xs">
            Best matches auto-selected
          </Typography>
        </div>
      </div>
    </div>
  );

};

IntentsPreviewFooter.propTypes = {
  handleAccept: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  setIsAutoResolving: PropTypes.func.isRequired,
  setSelectionStrategy: PropTypes.func.isRequired,
  isProjectEmpty: PropTypes.bool
};

IntentsPreviewFooter.displayName = 'IntentsPreviewFooter';
